export const PM_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export type PmVisitStatus = "completed" | "scheduled" | "pending";

export interface PmVisitRecord {
  id: string;
  year: number;
  month: string;
  dueDate: string;
  status: PmVisitStatus;
  requiredCount: number;
  completedCount: number;
  completedDate?: string;
  technician?: string;
  reportFileName?: string;
  reportSummary?: string;
}

export interface PmSite {
  id: string;
  siteName: string;
  city: string;
  propertyCode: string;
  frequency: string;
  assetCount: number;
  visits: PmVisitRecord[];
}

export interface PmService {
  id: string;
  serviceName: string;
  frequency: string;
  description: string;
  sites: PmSite[];
}

export interface PmProgramData {
  clientName: string;
  portfolioName: string;
  staticData: PmStaticDataRecord[];
  services: PmService[];
}

export interface PmStaticDataRecord {
  fixedScheduleReporting: string;
  frequency: string;
  totalSites: string;
  jan: number;
  feb: number;
  mar: number;
  apr: number;
  may: number;
  jun: number;
  jul: number;
  aug: number;
  sep: number;
  oct: number;
  nov: number;
  dec: number;
}

export interface PmCompliancePeriod {
  completed: number;
  total: number;
  label: string;
}

export interface PmServiceCompliance {
  serviceName: string;
  compliancePct: number;
}

export interface PmDashboardData {
  month: PmCompliancePeriod;
  ytd: PmCompliancePeriod;
  services: PmServiceCompliance[];
}

type PreventativeMaintenanceApiRecord = Record<string, any>;

const isRecord = (value: unknown): value is PreventativeMaintenanceApiRecord => {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
};

const unwrapPreventativeMaintenancePayload = (value: unknown): unknown => {
  if (!isRecord(value)) {
    return value;
  }

  const nestedKeys = ["data", "program", "preventativeMaintenance", "preventativeMaintenanceData", "payload", "result"];

  for (const key of nestedKeys) {
    const nestedValue = value[key];
    if (nestedValue !== undefined && nestedValue !== null) {
      return nestedValue;
    }
  }

  return value;
};

const normalizeText = (value: unknown, fallback = "") => {
  return typeof value === "string" ? value.trim() : fallback;
};

const normalizeNumber = (value: unknown, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeMonth = (value: unknown, fallbackDate?: string) => {
  const candidate = normalizeText(value);
  const monthMatch = PM_MONTHS.find((month) => month.toLowerCase() === candidate.slice(0, 3).toLowerCase());

  if (monthMatch) {
    return monthMatch;
  }

  if (fallbackDate) {
    const parsedDate = new Date(fallbackDate);
    if (!Number.isNaN(parsedDate.getTime())) {
      return PM_MONTHS[parsedDate.getMonth()];
    }
  }

  return PM_MONTHS[0];
};

const normalizeYear = (value: unknown, fallbackDate?: string) => {
  const parsedYear = Number(value);
  if (Number.isFinite(parsedYear) && parsedYear > 0) {
    return parsedYear;
  }

  if (fallbackDate) {
    const parsedDate = new Date(fallbackDate);
    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate.getFullYear();
    }
  }

  return new Date().getFullYear();
};

const makeRecordId = (...parts: Array<string | number | undefined>) =>
  parts
    .map((part) => normalizeText(part, String(part ?? "")))
    .filter(Boolean)
    .join("-")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

const normalizeVisitStatus = (status: unknown, completedCount: number, dueDate?: string): PmVisitStatus => {
  const normalizedStatus = normalizeText(status).toLowerCase();

  if (normalizedStatus === "completed" || normalizedStatus === "scheduled" || normalizedStatus === "pending") {
    return normalizedStatus;
  }

  if (completedCount > 0) {
    return "completed";
  }

  if (dueDate) {
    const parsedDate = new Date(dueDate);
    if (!Number.isNaN(parsedDate.getTime()) && parsedDate.getTime() < Date.now()) {
      return "pending";
    }
  }

  return "scheduled";
};

const normalizeVisitRecord = (
  rawVisit: PreventativeMaintenanceApiRecord,
  serviceId: string,
  siteId: string,
  index: number,
): PmVisitRecord => {
  const dueDate = normalizeText(
    rawVisit.dueDate ??
      rawVisit.targetDate ??
      rawVisit.scheduledDate ??
      rawVisit.plannedDate ??
      rawVisit.pmduedate ??
      rawVisit.targcompdate,
  );
  const completedDate = normalizeText(
    rawVisit.completedDate ??
      rawVisit.actualDate ??
      rawVisit.actfinish ??
      rawVisit.completedOn ??
      rawVisit.completeddate,
  );
  const completedCount = normalizeNumber(
    rawVisit.completedCount ??
      rawVisit.completed ??
      rawVisit.completedVisits ??
      rawVisit.closedCount ??
      (rawVisit.status === "completed" ? 1 : 0),
    0,
  );
  const requiredCount = normalizeNumber(
    rawVisit.requiredCount ??
      rawVisit.totalCount ??
      rawVisit.visitCount ??
      rawVisit.pmCount ??
      rawVisit.assetCount ??
      rawVisit.required ??
      1,
    1,
  );

  return {
    id: normalizeText(
      rawVisit.id ??
        rawVisit.visitId ??
        rawVisit.recordId ??
        rawVisit.workorderid ??
        makeRecordId(serviceId, siteId, rawVisit.year, rawVisit.month, index),
      makeRecordId(serviceId, siteId, rawVisit.year, rawVisit.month, index),
    ),
    year: normalizeYear(
      rawVisit.year ?? rawVisit.periodYear ?? rawVisit.visitYear ?? rawVisit.fiscalYear,
      dueDate || completedDate,
    ),
    month: normalizeMonth(
      rawVisit.month ?? rawVisit.periodMonth ?? rawVisit.visitMonth ?? rawVisit.monthName,
      dueDate || completedDate,
    ),
    dueDate: dueDate || completedDate || new Date().toISOString().slice(0, 10),
    status: normalizeVisitStatus(rawVisit.status, completedCount, dueDate || completedDate),
    requiredCount,
    completedCount,
    completedDate: completedDate || undefined,
    technician: normalizeText(
      rawVisit.technician ?? rawVisit.assignedTo ?? rawVisit.owner ?? rawVisit.contact ?? rawVisit.workgroup,
      undefined,
    ),
    reportFileName: normalizeText(rawVisit.reportFileName ?? rawVisit.reportName ?? rawVisit.documentName, undefined),
    reportSummary: normalizeText(
      rawVisit.reportSummary ?? rawVisit.summary ?? rawVisit.description ?? rawVisit.longDescription,
      undefined,
    ),
  };
};

const normalizeServiceRecord = (rawService: PreventativeMaintenanceApiRecord, index: number): PmService => {
  const serviceId = normalizeText(
    rawService.id ?? rawService.serviceId ?? rawService.serviceCode ?? rawService.name,
    makeRecordId("service", index),
  );
  const serviceName = normalizeText(
    rawService.serviceName ?? rawService.name ?? rawService.description ?? rawService.type,
    `Service ${index + 1}`,
  );
  const sitesSource = Array.isArray(rawService.sites)
    ? rawService.sites
    : Array.isArray(rawService.locations)
      ? rawService.locations
      : [];

  return {
    id: serviceId || makeRecordId("service", index),
    serviceName,
    frequency: normalizeText(rawService.frequency ?? rawService.serviceFrequency ?? rawService.interval, "Monthly"),
    description: normalizeText(rawService.description ?? rawService.longDescription ?? rawService.summary, ""),
    sites: sitesSource.map((rawSite: PreventativeMaintenanceApiRecord, siteIndex: number) => {
      const visitsSource = Array.isArray(rawSite.visits)
        ? rawSite.visits
        : Array.isArray(rawSite.periods)
          ? rawSite.periods
          : Array.isArray(rawSite.records)
            ? rawSite.records
            : [];

      return {
        id: normalizeText(
          rawSite.id ?? rawSite.siteId ?? rawSite.locationId ?? rawSite.propertyCode ?? rawSite.siteName,
          makeRecordId(serviceId, "site", siteIndex),
        ),
        siteName: normalizeText(
          rawSite.siteName ?? rawSite.name ?? rawSite.locationName ?? rawSite.description,
          `Site ${siteIndex + 1}`,
        ),
        city: normalizeText(rawSite.city ?? rawSite.siteCity ?? rawSite.locationCity ?? rawSite.addressCity, ""),
        propertyCode: normalizeText(
          rawSite.propertyCode ?? rawSite.code ?? rawSite.locationCode ?? rawSite.assetCode,
          "",
        ),
        frequency: normalizeText(rawSite.frequency ?? rawSite.serviceFrequency ?? rawService.frequency, "Monthly"),
        assetCount: normalizeNumber(rawSite.assetCount ?? rawSite.totalAssets ?? rawSite.count, 0),
        visits: visitsSource.map((rawVisit: PreventativeMaintenanceApiRecord, visitIndex: number) =>
          normalizeVisitRecord(
            rawVisit,
            serviceId,
            normalizeText(
              rawSite.id ?? rawSite.siteId ?? rawSite.locationId ?? rawSite.propertyCode ?? rawSite.siteName,
              makeRecordId(serviceId, "site", siteIndex),
            ),
            visitIndex,
          ),
        ),
      };
    }),
  };
};

const normalizeStaticDataRecord = (rawRecord: PreventativeMaintenanceApiRecord): PmStaticDataRecord => ({
  fixedScheduleReporting: normalizeText(
    rawRecord.fixedScheduleReporting ?? rawRecord.serviceName ?? rawRecord.name ?? rawRecord.description,
    "",
  ),
  frequency: normalizeText(rawRecord.frequency ?? rawRecord.serviceFrequency ?? rawRecord.interval, ""),
  totalSites: normalizeText(rawRecord.totalSites ?? rawRecord.totalSite ?? rawRecord.siteCount, ""),
  jan: normalizeNumber(rawRecord.jan, 0),
  feb: normalizeNumber(rawRecord.feb, 0),
  mar: normalizeNumber(rawRecord.mar, 0),
  apr: normalizeNumber(rawRecord.apr, 0),
  may: normalizeNumber(rawRecord.may, 0),
  jun: normalizeNumber(rawRecord.jun, 0),
  jul: normalizeNumber(rawRecord.jul, 0),
  aug: normalizeNumber(rawRecord.aug, 0),
  sep: normalizeNumber(rawRecord.sep, 0),
  oct: normalizeNumber(rawRecord.oct, 0),
  nov: normalizeNumber(rawRecord.nov, 0),
  dec: normalizeNumber(rawRecord.dec, 0),
});

const extractStaticData = (source: unknown): PmStaticDataRecord[] => {
  if (!isRecord(source)) {
    return [];
  }

  const staticRows = [source.staticData, source.staticPmTab].find(Array.isArray);
  if (!Array.isArray(staticRows)) {
    return [];
  }

  return staticRows.filter(isRecord).map((row) => normalizeStaticDataRecord(row));
};

const extractFlatRecords = (value: unknown): PreventativeMaintenanceApiRecord[] => {
  if (Array.isArray(value)) {
    return value.filter(isRecord);
  }

  if (!isRecord(value)) {
    return [];
  }

  const recordArrays = [value.records, value.items, value.rows, value.visits, value.services, value.data].find(
    Array.isArray,
  );
  if (Array.isArray(recordArrays)) {
    return recordArrays.filter(isRecord);
  }

  return [value];
};

export const normalizePreventativeMaintenanceProgram = (response: unknown, clientId?: string): PmProgramData => {
  const source = unwrapPreventativeMaintenancePayload(response);
  const fallbackProgram = getProgram(clientId);
  const staticData = extractStaticData(source);

  if (!source) {
    return fallbackProgram;
  }

  if (isRecord(source) && Array.isArray(source.services)) {
    const programServices = source.services
      .filter(isRecord)
      .map((service, index) => normalizeServiceRecord(service, index));

    return {
      clientName: normalizeText(
        source.clientName ?? source.client ?? source.clientNameDisplay,
        fallbackProgram.clientName,
      ),
      portfolioName: normalizeText(
        source.portfolioName ?? source.portfolio ?? source.projectName,
        fallbackProgram.portfolioName,
      ),
      staticData,
      services: programServices,
    };
  }

  const flatRecords = extractFlatRecords(source);

  if (flatRecords.length === 0) {
    return {
      ...fallbackProgram,
      staticData,
    };
  }

  const serviceMap = new Map<
    string,
    {
      serviceName: string;
      frequency: string;
      description: string;
      sites: Map<
        string,
        {
          id: string;
          siteName: string;
          city: string;
          propertyCode: string;
          frequency: string;
          assetCount: number;
          visits: PmVisitRecord[];
        }
      >;
    }
  >();

  flatRecords.forEach((record, index) => {
    const serviceName = normalizeText(
      record.serviceName ?? record.service ?? record.category ?? record.pmService ?? record.name,
      "Preventative Maintenance",
    );
    const serviceKey = makeRecordId(serviceName, record.serviceCode ?? record.serviceId ?? index);
    const siteName = normalizeText(
      record.siteName ?? record.site ?? record.locationName ?? record.location ?? record.propertyName,
      `Site ${index + 1}`,
    );
    const siteKey = makeRecordId(serviceKey, siteName, record.siteId ?? record.locationId ?? index);

    if (!serviceMap.has(serviceKey)) {
      serviceMap.set(serviceKey, {
        serviceName,
        frequency: normalizeText(record.frequency ?? record.serviceFrequency ?? record.interval, "Monthly"),
        description: normalizeText(record.description ?? record.longDescription ?? record.summary, ""),
        sites: new Map(),
      });
    }

    const serviceEntry = serviceMap.get(serviceKey);
    if (!serviceEntry) {
      return;
    }

    if (!serviceEntry.sites.has(siteKey)) {
      serviceEntry.sites.set(siteKey, {
        id: normalizeText(record.siteId ?? record.locationId ?? record.propertyCode ?? siteKey, siteKey),
        siteName,
        city: normalizeText(record.city ?? record.siteCity ?? record.locationCity, ""),
        propertyCode: normalizeText(record.propertyCode ?? record.locationCode ?? record.siteCode, ""),
        frequency: normalizeText(
          record.frequency ?? record.serviceFrequency ?? record.interval,
          serviceEntry.frequency,
        ),
        assetCount: normalizeNumber(record.assetCount ?? record.totalAssets ?? record.count, 0),
        visits: [],
      });
    }

    const siteEntry = serviceEntry.sites.get(siteKey);
    if (!siteEntry) {
      return;
    }

    siteEntry.visits.push(normalizeVisitRecord(record, serviceKey, siteKey, siteEntry.visits.length));
  });

  return {
    clientName: fallbackProgram.clientName,
    portfolioName: fallbackProgram.portfolioName,
    staticData,
    services: Array.from(serviceMap.entries()).map(([serviceId, serviceEntry]) => ({
      id: serviceId,
      serviceName: serviceEntry.serviceName,
      frequency: serviceEntry.frequency,
      description: serviceEntry.description,
      sites: Array.from(serviceEntry.sites.values()),
    })),
  };
};

const DEFAULT_PM_PROGRAM: PmProgramData = {
  clientName: "Kleenway Client Portfolio",
  portfolioName: "Ontario Mixed Portfolio",
  staticData: [],
  services: [
    {
      id: "hvac",
      serviceName: "HVAC",
      frequency: "Bi-Annual",
      description: "Seasonal rooftop unit inspection, filter changes, and startup checks.",
      sites: [
        {
          id: "toronto-west",
          siteName: "Toronto West Campus",
          city: "Toronto, ON",
          propertyCode: "TOR-W01",
          frequency: "Bi-Annual",
          assetCount: 12,
          visits: [
            {
              id: "hvac-tor-jan",
              year: 2026,
              month: "Jan",
              dueDate: "2026-01-18",
              completedDate: "2026-01-16",
              status: "completed",
              requiredCount: 1,
              completedCount: 1,
              technician: "A. Morgan",
              reportFileName: "HVAC-Toronto-West-Jan-2026.txt",
              reportSummary: "Winter PM completed. Filters changed and belt wear within tolerance.",
            },
            {
              id: "hvac-tor-jul",
              year: 2026,
              month: "Jul",
              dueDate: "2026-07-22",
              status: "scheduled",
              requiredCount: 1,
              completedCount: 0,
              technician: "S. Patel",
              reportSummary: "Summer PM visit scheduled with rooftop access confirmed.",
            },
          ],
        },
        {
          id: "mississauga-centre",
          siteName: "Mississauga Service Centre",
          city: "Mississauga, ON",
          propertyCode: "MIS-C14",
          frequency: "Bi-Annual",
          assetCount: 8,
          visits: [
            {
              id: "hvac-mis-feb",
              year: 2026,
              month: "Feb",
              dueDate: "2026-02-08",
              completedDate: "2026-02-07",
              status: "completed",
              requiredCount: 1,
              completedCount: 1,
              technician: "J. Sinclair",
              reportFileName: "HVAC-Mississauga-Feb-2026.txt",
              reportSummary: "Completed winter PM. Condenser coils cleaned and controls verified.",
            },
            {
              id: "hvac-mis-aug",
              year: 2026,
              month: "Aug",
              dueDate: "2026-08-12",
              status: "pending",
              requiredCount: 1,
              completedCount: 0,
              reportSummary: "Awaiting tenant coordination for after-hours service window.",
            },
          ],
        },
      ],
    },
    {
      id: "backflow",
      serviceName: "Backflow Preventors",
      frequency: "Annual",
      description: "Annual testing, certification, and compliance submission.",
      sites: [
        {
          id: "hamilton-hub",
          siteName: "Hamilton Distribution Hub",
          city: "Hamilton, ON",
          propertyCode: "HAM-D02",
          frequency: "Annual",
          assetCount: 4,
          visits: [
            {
              id: "backflow-ham-mar",
              year: 2026,
              month: "Mar",
              dueDate: "2026-03-14",
              completedDate: "2026-03-14",
              status: "completed",
              requiredCount: 1,
              completedCount: 1,
              technician: "L. Ferreira",
              reportFileName: "Backflow-Hamilton-Mar-2026.txt",
              reportSummary: "Annual certification completed and filed with municipality.",
            },
          ],
        },
        {
          id: "ottawa-plaza",
          siteName: "Ottawa Plaza",
          city: "Ottawa, ON",
          propertyCode: "OTT-P11",
          frequency: "Annual",
          assetCount: 3,
          visits: [
            {
              id: "backflow-ott-apr",
              year: 2026,
              month: "Apr",
              dueDate: "2026-04-21",
              completedDate: "2026-04-20",
              status: "completed",
              requiredCount: 1,
              completedCount: 1,
              technician: "M. Green",
              reportFileName: "Backflow-Ottawa-Apr-2026.txt",
              reportSummary: "No deficiencies observed. Tags and test records updated.",
            },
          ],
        },
      ],
    },
    {
      id: "pest-control",
      serviceName: "Pest Control",
      frequency: "Monthly",
      description: "Routine bait station, trap, and sanitation inspection program.",
      sites: [
        {
          id: "scarborough-office",
          siteName: "Scarborough Office Park",
          city: "Scarborough, ON",
          propertyCode: "SCA-O19",
          frequency: "Monthly",
          assetCount: 1,
          visits: [
            {
              id: "pest-sca-may",
              year: 2026,
              month: "May",
              dueDate: "2026-05-05",
              completedDate: "2026-05-05",
              status: "completed",
              requiredCount: 1,
              completedCount: 1,
              technician: "P. Silva",
              reportFileName: "Pest-Scarborough-May-2026.txt",
              reportSummary: "Monthly route completed. No active activity noted in common areas.",
            },
            {
              id: "pest-sca-jun",
              year: 2026,
              month: "Jun",
              dueDate: "2026-06-06",
              completedDate: "2026-06-06",
              status: "completed",
              requiredCount: 1,
              completedCount: 1,
              technician: "P. Silva",
              reportFileName: "Pest-Scarborough-Jun-2026.txt",
              reportSummary: "Follow-up route complete. Exterior bait stations refreshed.",
            },
            {
              id: "pest-sca-jul",
              year: 2026,
              month: "Jul",
              dueDate: "2026-07-09",
              status: "scheduled",
              requiredCount: 1,
              completedCount: 0,
              technician: "P. Silva",
              reportSummary: "July route scheduled with tenant notice sent.",
            },
          ],
        },
        {
          id: "markham-campus",
          siteName: "Markham Campus",
          city: "Markham, ON",
          propertyCode: "MAR-C07",
          frequency: "Monthly",
          assetCount: 1,
          visits: [
            {
              id: "pest-mar-may",
              year: 2026,
              month: "May",
              dueDate: "2026-05-11",
              completedDate: "2026-05-11",
              status: "completed",
              requiredCount: 1,
              completedCount: 1,
              technician: "R. Thomas",
              reportFileName: "Pest-Markham-May-2026.txt",
              reportSummary: "No new findings. Service log posted onsite.",
            },
            {
              id: "pest-mar-jun",
              year: 2026,
              month: "Jun",
              dueDate: "2026-06-10",
              completedDate: "2026-06-10",
              status: "completed",
              requiredCount: 1,
              completedCount: 1,
              technician: "R. Thomas",
              reportFileName: "Pest-Markham-Jun-2026.txt",
              reportSummary: "Monthly route complete. Kitchen area traps reset.",
            },
            {
              id: "pest-mar-jul",
              year: 2026,
              month: "Jul",
              dueDate: "2026-07-15",
              status: "pending",
              requiredCount: 1,
              completedCount: 0,
              reportSummary: "Waiting on security escort confirmation.",
            },
          ],
        },
      ],
    },
    {
      id: "fire-dampers",
      serviceName: "Fire Dampers",
      frequency: "Annual",
      description: "Inspection and damper function testing for life-safety compliance.",
      sites: [
        {
          id: "kitchener-tower",
          siteName: "Kitchener Tower",
          city: "Kitchener, ON",
          propertyCode: "KIT-T04",
          frequency: "Annual",
          assetCount: 15,
          visits: [
            {
              id: "fd-kit-may",
              year: 2026,
              month: "May",
              dueDate: "2026-05-28",
              completedDate: "2026-05-29",
              status: "completed",
              requiredCount: 1,
              completedCount: 1,
              technician: "C. Barnes",
              reportFileName: "Fire-Dampers-Kitchener-May-2026.txt",
              reportSummary: "Testing complete. Two units flagged for seal replacement under corrective work.",
            },
          ],
        },
        {
          id: "london-centre",
          siteName: "London Civic Centre",
          city: "London, ON",
          propertyCode: "LON-C03",
          frequency: "Annual",
          assetCount: 9,
          visits: [
            {
              id: "fd-lon-sep",
              year: 2026,
              month: "Sep",
              dueDate: "2026-09-17",
              status: "pending",
              requiredCount: 1,
              completedCount: 0,
              reportSummary: "Access plan pending from building operations.",
            },
          ],
        },
      ],
    },
  ],
};

const ENBRIDGE_PM_PROGRAM: PmProgramData = {
  clientName: "Enbridge Gas Inc.",
  portfolioName: "Ontario Properties",
  staticData: [],
  services: [
    {
      id: "backflow-enb",
      serviceName: "Backflow Preventors",
      frequency: "Annual",
      description: "Annual testing and municipal certification program.",
      sites: [
        {
          id: "enb-north-york",
          siteName: "North York Operations",
          city: "North York, ON",
          propertyCode: "ENB-NY1",
          frequency: "Annual",
          assetCount: 5,
          visits: [
            {
              id: "enb-bf-ny-jan",
              year: 2026,
              month: "Jan",
              dueDate: "2026-01-18",
              completedDate: "2026-01-16",
              status: "completed",
              requiredCount: 1,
              completedCount: 1,
              technician: "L. Ferreira",
              reportFileName: "ENB-Backflow-NorthYork-Jan-2026.txt",
              reportSummary: "Annual backflow certification complete and submitted.",
            },
          ],
        },
        {
          id: "enb-brampton",
          siteName: "Brampton Fleet Yard",
          city: "Brampton, ON",
          propertyCode: "ENB-BR2",
          frequency: "Annual",
          assetCount: 5,
          visits: [
            {
              id: "enb-bf-br-mar",
              year: 2026,
              month: "Mar",
              dueDate: "2026-03-05",
              completedDate: "2026-03-05",
              status: "completed",
              requiredCount: 2,
              completedCount: 2,
              technician: "M. Green",
              reportFileName: "ENB-Backflow-Brampton-Mar-2026.txt",
              reportSummary: "Two assemblies tested successfully. Documentation uploaded.",
            },
          ],
        },
      ],
    },
    {
      id: "bmt",
      serviceName: "BMT Inspections",
      frequency: "Monthly",
      description: "Monthly building maintenance tour and compliance inspection.",
      sites: [
        {
          id: "enb-donmills",
          siteName: "Don Mills Campus",
          city: "Toronto, ON",
          propertyCode: "ENB-DM3",
          frequency: "Monthly",
          assetCount: 84,
          visits: [
            {
              id: "enb-bmt-jan",
              year: 2026,
              month: "Jan",
              dueDate: "2026-01-31",
              completedDate: "2026-01-29",
              status: "completed",
              requiredCount: 42,
              completedCount: 42,
              technician: "Ops Team A",
              reportFileName: "ENB-BMT-DonMills-Jan-2026.txt",
              reportSummary: "Monthly BMT completed with all checkpoints closed.",
            },
            {
              id: "enb-bmt-feb",
              year: 2026,
              month: "Feb",
              dueDate: "2026-02-28",
              completedDate: "2026-02-26",
              status: "completed",
              requiredCount: 42,
              completedCount: 42,
              technician: "Ops Team A",
              reportFileName: "ENB-BMT-DonMills-Feb-2026.txt",
              reportSummary: "Monthly BMT completed. No critical deficiencies.",
            },
            {
              id: "enb-bmt-mar",
              year: 2026,
              month: "Mar",
              dueDate: "2026-03-31",
              completedDate: "2026-03-30",
              status: "completed",
              requiredCount: 42,
              completedCount: 42,
              technician: "Ops Team A",
              reportFileName: "ENB-BMT-DonMills-Mar-2026.txt",
              reportSummary: "March BMT complete. Minor housekeeping notes recorded.",
            },
          ],
        },
        {
          id: "enb-windsor",
          siteName: "Windsor Regional Office",
          city: "Windsor, ON",
          propertyCode: "ENB-WI4",
          frequency: "Monthly",
          assetCount: 84,
          visits: [
            {
              id: "enb-bmt-apr",
              year: 2026,
              month: "Apr",
              dueDate: "2026-04-30",
              completedDate: "2026-04-29",
              status: "completed",
              requiredCount: 42,
              completedCount: 42,
              technician: "Ops Team B",
              reportFileName: "ENB-BMT-Windsor-Apr-2026.txt",
              reportSummary: "April BMT completed with all safety checks passed.",
            },
            {
              id: "enb-bmt-may",
              year: 2026,
              month: "May",
              dueDate: "2026-05-31",
              completedDate: "2026-05-29",
              status: "completed",
              requiredCount: 42,
              completedCount: 42,
              technician: "Ops Team B",
              reportFileName: "ENB-BMT-Windsor-May-2026.txt",
              reportSummary: "May BMT completed. Exterior lighting observations logged.",
            },
            {
              id: "enb-bmt-jun",
              year: 2026,
              month: "Jun",
              dueDate: "2026-06-30",
              completedDate: "2026-06-27",
              status: "completed",
              requiredCount: 42,
              completedCount: 42,
              technician: "Ops Team B",
              reportFileName: "ENB-BMT-Windsor-Jun-2026.txt",
              reportSummary: "June BMT closed with zero outstanding actions.",
            },
            {
              id: "enb-bmt-jul",
              year: 2026,
              month: "Jul",
              dueDate: "2026-07-30",
              status: "scheduled",
              requiredCount: 42,
              completedCount: 0,
              technician: "Ops Team B",
              reportSummary: "July BMT route scheduled for week 4.",
            },
          ],
        },
      ],
    },
    {
      id: "co-no2",
      serviceName: "CO/NO2 Sensors",
      frequency: "Annual",
      description: "Sensor calibration and alarm verification program.",
      sites: [
        {
          id: "enb-hamilton-garage",
          siteName: "Hamilton Vehicle Garage",
          city: "Hamilton, ON",
          propertyCode: "ENB-HA5",
          frequency: "Annual",
          assetCount: 2,
          visits: [
            {
              id: "enb-co-jan",
              year: 2026,
              month: "Jan",
              dueDate: "2026-01-11",
              completedDate: "2026-01-11",
              status: "completed",
              requiredCount: 1,
              completedCount: 1,
              technician: "C. Reid",
              reportFileName: "ENB-CONO2-Hamilton-Jan-2026.txt",
              reportSummary: "Calibration complete. Sensors within tolerance.",
            },
          ],
        },
        {
          id: "enb-oshawa-garage",
          siteName: "Oshawa Fleet Garage",
          city: "Oshawa, ON",
          propertyCode: "ENB-OS6",
          frequency: "Annual",
          assetCount: 2,
          visits: [
            {
              id: "enb-co-feb",
              year: 2026,
              month: "Feb",
              dueDate: "2026-02-15",
              completedDate: "2026-02-15",
              status: "completed",
              requiredCount: 1,
              completedCount: 1,
              technician: "C. Reid",
              reportFileName: "ENB-CONO2-Oshawa-Feb-2026.txt",
              reportSummary: "Annual calibration complete. Alarm tests passed.",
            },
          ],
        },
      ],
    },
    {
      id: "compressors",
      serviceName: "Compressors",
      frequency: "Bi-Annual",
      description: "Operational and safety inspection for compressed air systems.",
      sites: [
        {
          id: "enb-london-yard",
          siteName: "London Service Yard",
          city: "London, ON",
          propertyCode: "ENB-LO7",
          frequency: "Bi-Annual",
          assetCount: 1,
          visits: [
            {
              id: "enb-comp-apr",
              year: 2026,
              month: "Apr",
              dueDate: "2026-04-08",
              completedDate: "2026-04-08",
              status: "completed",
              requiredCount: 1,
              completedCount: 1,
              technician: "J. Sinclair",
              reportFileName: "ENB-Compressors-London-Apr-2026.txt",
              reportSummary: "Spring inspection completed. Pressure relief devices tested.",
            },
          ],
        },
      ],
    },
    {
      id: "custodial",
      serviceName: "Custodial Audits",
      frequency: "Monthly",
      description: "Routine janitorial inspection and service quality audit.",
      sites: [
        {
          id: "enb-mississauga-hq",
          siteName: "Mississauga Headquarters",
          city: "Mississauga, ON",
          propertyCode: "ENB-MI8",
          frequency: "Monthly",
          assetCount: 35,
          visits: [
            {
              id: "enb-cust-jan",
              year: 2026,
              month: "Jan",
              dueDate: "2026-01-26",
              completedDate: "2026-01-26",
              status: "completed",
              requiredCount: 35,
              completedCount: 35,
              technician: "Audit Team",
              reportFileName: "ENB-Custodial-Mississauga-Jan-2026.txt",
              reportSummary: "Audit complete. All critical washroom and lobby zones passed.",
            },
            {
              id: "enb-cust-feb",
              year: 2026,
              month: "Feb",
              dueDate: "2026-02-26",
              completedDate: "2026-02-26",
              status: "completed",
              requiredCount: 31,
              completedCount: 31,
              technician: "Audit Team",
              reportFileName: "ENB-Custodial-Mississauga-Feb-2026.txt",
              reportSummary: "February audit complete. One low-priority consumable note.",
            },
            {
              id: "enb-cust-mar",
              year: 2026,
              month: "Mar",
              dueDate: "2026-03-27",
              completedDate: "2026-03-27",
              status: "completed",
              requiredCount: 33,
              completedCount: 33,
              technician: "Audit Team",
              reportFileName: "ENB-Custodial-Mississauga-Mar-2026.txt",
              reportSummary: "March audit complete. Cleaning quality stable across portfolio.",
            },
            {
              id: "enb-cust-apr",
              year: 2026,
              month: "Apr",
              dueDate: "2026-04-24",
              completedDate: "2026-04-24",
              status: "completed",
              requiredCount: 34,
              completedCount: 34,
              technician: "Audit Team",
              reportFileName: "ENB-Custodial-Mississauga-Apr-2026.txt",
              reportSummary: "April audit completed. Vendor performance on target.",
            },
            {
              id: "enb-cust-may",
              year: 2026,
              month: "May",
              dueDate: "2026-05-29",
              completedDate: "2026-05-29",
              status: "completed",
              requiredCount: 38,
              completedCount: 38,
              technician: "Audit Team",
              reportFileName: "ENB-Custodial-Mississauga-May-2026.txt",
              reportSummary: "May audit complete. Entrance matting program improved scores.",
            },
            {
              id: "enb-cust-jul",
              year: 2026,
              month: "Jul",
              dueDate: "2026-07-24",
              status: "scheduled",
              requiredCount: 36,
              completedCount: 0,
              technician: "Audit Team",
              reportSummary: "July audit window booked with site contact.",
            },
          ],
        },
      ],
    },
    {
      id: "esa",
      serviceName: "ESA",
      frequency: "Annual",
      description: "Electrical safety inspection and compliance verification.",
      sites: [
        {
          id: "enb-sudbury",
          siteName: "Sudbury Operations",
          city: "Sudbury, ON",
          propertyCode: "ENB-SU9",
          frequency: "Annual",
          assetCount: 8,
          visits: [
            {
              id: "enb-esa-jan",
              year: 2026,
              month: "Jan",
              dueDate: "2026-01-10",
              completedDate: "2026-01-09",
              status: "completed",
              requiredCount: 1,
              completedCount: 1,
              technician: "D. Cole",
              reportFileName: "ENB-ESA-Sudbury-Jan-2026.txt",
              reportSummary: "ESA inspection closed with no deficiencies.",
            },
            {
              id: "enb-esa-feb",
              year: 2026,
              month: "Feb",
              dueDate: "2026-02-10",
              completedDate: "2026-02-10",
              status: "completed",
              requiredCount: 3,
              completedCount: 3,
              technician: "D. Cole",
              reportFileName: "ENB-ESA-Sudbury-Feb-2026.txt",
              reportSummary: "Supplemental ESA scope completed. Corrective items closed.",
            },
            {
              id: "enb-esa-mar",
              year: 2026,
              month: "Mar",
              dueDate: "2026-03-12",
              completedDate: "2026-03-12",
              status: "completed",
              requiredCount: 1,
              completedCount: 1,
              technician: "D. Cole",
              reportFileName: "ENB-ESA-Sudbury-Mar-2026.txt",
              reportSummary: "Follow-up verification complete.",
            },
            {
              id: "enb-esa-apr",
              year: 2026,
              month: "Apr",
              dueDate: "2026-04-14",
              completedDate: "2026-04-14",
              status: "completed",
              requiredCount: 1,
              completedCount: 1,
              technician: "D. Cole",
              reportFileName: "ENB-ESA-Sudbury-Apr-2026.txt",
              reportSummary: "Annual ESA inspection cycle confirmed complete.",
            },
            {
              id: "enb-esa-may",
              year: 2026,
              month: "May",
              dueDate: "2026-05-15",
              completedDate: "2026-05-15",
              status: "completed",
              requiredCount: 2,
              completedCount: 2,
              technician: "D. Cole",
              reportFileName: "ENB-ESA-Sudbury-May-2026.txt",
              reportSummary: "Post-inspection remedial verification completed.",
            },
          ],
        },
      ],
    },
  ],
};

const normalizeClientId = (clientId?: string) => (clientId ?? "").trim().toUpperCase();

const getProgram = (clientId?: string) => {
  return normalizeClientId(clientId) === "ENBRIDGE" ? ENBRIDGE_PM_PROGRAM : DEFAULT_PM_PROGRAM;
};

const getVisitMonthIndex = (month: string) => PM_MONTHS.indexOf(month);

const getLatestPeriod = (program: PmProgramData) => {
  const visits = program.services.flatMap((service) => service.sites.flatMap((site) => site.visits));

  return visits.reduce(
    (latest, visit) => {
      const visitIndex = getVisitMonthIndex(visit.month);
      const latestIndex = getVisitMonthIndex(latest.month);

      if (visit.year > latest.year || (visit.year === latest.year && visitIndex > latestIndex)) {
        return { year: visit.year, month: visit.month };
      }

      return latest;
    },
    { year: 2026, month: "Jul" },
  );
};

export const getPreventativeMaintenanceProgram = (clientId?: string) => getProgram(clientId);

export const getPmDashboardData = (clientId?: string, programOverride?: PmProgramData): PmDashboardData => {
  const program = programOverride ?? getProgram(clientId);
  const latestPeriod = getLatestPeriod(program);

  let monthCompleted = 0;
  let monthTotal = 0;
  let ytdCompleted = 0;
  let ytdTotal = 0;

  const services = program.services.map((service) => {
    const serviceTotals = service.sites
      .flatMap((site) => site.visits)
      .reduce(
        (totals, visit) => {
          totals.completed += visit.completedCount;
          totals.required += visit.requiredCount;

          if (visit.year === latestPeriod.year && visit.month === latestPeriod.month) {
            monthCompleted += visit.completedCount;
            monthTotal += visit.requiredCount;
          }

          return totals;
        },
        { completed: 0, required: 0 },
      );

    ytdCompleted += serviceTotals.completed;
    ytdTotal += serviceTotals.required;

    return {
      serviceName: service.serviceName,
      compliancePct: serviceTotals.required ? Math.round((serviceTotals.completed / serviceTotals.required) * 100) : 0,
    };
  });

  return {
    month: {
      completed: monthCompleted,
      total: monthTotal,
      label: `${latestPeriod.month} ${latestPeriod.year}`,
    },
    ytd: {
      completed: ytdCompleted,
      total: ytdTotal,
      label: `${latestPeriod.year} YTD`,
    },
    services,
  };
};

export const buildPmReportDownload = ({
  clientName,
  serviceName,
  siteName,
  visit,
}: {
  clientName: string;
  serviceName: string;
  siteName: string;
  visit: PmVisitRecord;
}) => {
  const body = [
    `Client: ${clientName}`,
    `Service: ${serviceName}`,
    `Site: ${siteName}`,
    `Period: ${visit.month} ${visit.year}`,
    `Status: ${visit.status}`,
    `Due Date: ${visit.dueDate}`,
    `Completed Date: ${visit.completedDate ?? "Not completed"}`,
    `Technician: ${visit.technician ?? "Not assigned"}`,
    `Completed Scope: ${visit.completedCount} of ${visit.requiredCount}`,
    "",
    visit.reportSummary ?? "No report summary available.",
  ].join("\n");

  return `data:text/plain;charset=utf-8,${encodeURIComponent(body)}`;
};
